import { useEffect, useState } from "react";
import { AlertCircle, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import { useUserRole } from "@/hooks/useUserRole";
import { useAdminAuthSettings, type AdminAuthSettings } from "@/hooks/useAdminAuthSettings";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";

type Draft = Pick<AdminAuthSettings, "passwordHibpEnabled" | "anonymousSignInsEnabled" | "autoConfirmEmail">;

function toDraft(s: AdminAuthSettings): Draft {
  return {
    passwordHibpEnabled: s.passwordHibpEnabled,
    anonymousSignInsEnabled: s.anonymousSignInsEnabled,
    autoConfirmEmail: s.autoConfirmEmail,
  };
}

function draftDiff(a: Draft, b: Draft): Partial<Draft> {
  const diff: Partial<Draft> = {};
  (Object.keys(a) as (keyof Draft)[]).forEach((k) => {
    if (a[k] !== b[k]) diff[k] = a[k];
  });
  return diff;
}

const FIELDS: Array<{
  key: keyof Draft;
  title: string;
  description: string;
  helpWhenOn: string;
  helpWhenOff: string;
}> = [
  {
    key: "passwordHibpEnabled",
    title: "HIBP leaked-password check",
    description:
      "Rejects passwords found in the Have I Been Pwned breach corpus at signup and password change.",
    helpWhenOn: "Users cannot pick a password that has appeared in a known breach.",
    helpWhenOff: "Any password meeting the minimum length is accepted, even if it has been breached.",
  },
  {
    key: "anonymousSignInsEnabled",
    title: "Anonymous sign-ins",
    description: "Allow guest sessions without an email address.",
    helpWhenOn: "Visitors can create disposable, unauthenticated user rows.",
    helpWhenOff: "Every session must be tied to a real email account.",
  },
  {
    key: "autoConfirmEmail",
    title: "Auto-confirm email addresses",
    description: "Skip the email verification click after signup.",
    helpWhenOn: "New accounts are usable immediately without verifying ownership of the address.",
    helpWhenOff: "Users must click the verification link before they can sign in.",
  },
];

export function AuthSettingsPanel() {
  const { isSuperAdmin } = useUserRole();
  const { status, settings, error, saving, refetch, save } = useAdminAuthSettings();
  const [draft, setDraft] = useState<Draft | null>(null);

  useEffect(() => {
    if (settings) setDraft(toDraft(settings));
  }, [settings]);

  const dirty = draft && settings ? Object.keys(draftDiff(draft, toDraft(settings))).length > 0 : false;

  const handleSave = async () => {
    if (!draft || !settings) return;
    const patch = draftDiff(draft, toDraft(settings));
    if (Object.keys(patch).length === 0) return;
    try {
      await save(patch);
      toast({
        title: "Auth settings saved",
        description: "Changes are live in the app immediately.",
      });
    } catch (err) {
      toast({
        title: "Save failed",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleReset = () => {
    if (settings) setDraft(toDraft(settings));
  };

  if (status === "loading") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Auth flags</CardTitle>
          <CardDescription>Loading current values…</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (status === "error") {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Couldn't load auth settings</AlertTitle>
        <AlertDescription className="space-y-3">
          <p>{error?.message ?? "Unknown error."}</p>
          <Button size="sm" variant="outline" onClick={() => void refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" /> Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (!draft || !settings) return null;

  const readOnly = !isSuperAdmin;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <ShieldCheck className="h-5 w-5 text-primary" aria-hidden="true" />
            </div>
            <div>
              <CardTitle>Auth flags</CardTitle>
              <CardDescription>
                Controls the security posture the app enforces on the client. These values are also
                mirrored to the backend once the Supabase Management API sync is wired up.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {readOnly && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Read-only</AlertTitle>
              <AlertDescription>
                Only super admins can change these values. You can view them here.
              </AlertDescription>
            </Alert>
          )}

          {FIELDS.map((field) => {
            const value = draft[field.key];
            const id = `auth-flag-${field.key}`;
            return (
              <div
                key={field.key}
                className="flex items-start justify-between gap-6 rounded-lg border border-border p-4"
              >
                <div className="space-y-1">
                  <Label htmlFor={id} className="text-base font-medium">
                    {field.title}
                  </Label>
                  <p className="text-sm text-muted-foreground">{field.description}</p>
                  <p className="text-xs text-muted-foreground">
                    {value ? field.helpWhenOn : field.helpWhenOff}
                  </p>
                </div>
                <Switch
                  id={id}
                  checked={value}
                  disabled={readOnly || saving}
                  onCheckedChange={(checked) =>
                    setDraft((prev) => (prev ? { ...prev, [field.key]: checked } : prev))
                  }
                  aria-label={field.title}
                />
              </div>
            );
          })}

          <div className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-muted-foreground">
              Last updated {new Date(settings.updatedAt).toLocaleString()}
              {settings.updatedBy ? " • by super admin" : ""}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={!dirty || saving || readOnly}
              >
                Discard
              </Button>
              <Button onClick={handleSave} disabled={!dirty || saving || readOnly}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {saving ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Lovable Cloud auth config</AlertTitle>
        <AlertDescription>
          These toggles drive the in-app enforcement (signup HIBP check, UI gates, and Settings
          display). The matching backend auth flags are managed by Lovable Cloud and are kept in
          sync separately. Contact support or ask the AI if you need to change the backend defaults.
        </AlertDescription>
      </Alert>
    </div>
  );
}

export default AuthSettingsPanel;
